import { BaseWidgetRenderer } from './base-widget-renderer';
import { IImageWidgetStyle, ImageWidgetJsonData, WidgetRenderOptions } from '../../models/element-types';

export class ImageWidgetRenderer extends BaseWidgetRenderer<ImageWidgetJsonData> {
    protected override get defaultData(): ImageWidgetJsonData {
        return {
            id: '',
            type: 'image',
            src: '',
            alt: '',
            style: { height: '200px', heightFixed: false },
        };
    }

    protected renderContent(widgetJsonData: ImageWidgetJsonData, options: WidgetRenderOptions): HTMLElement {
        const content = document.createElement('div');
        content.classList.add('element-content');

        if (widgetJsonData.src) {
            this.removePlaceholder(content);
            const resolvedSrc = this.resolveUrl(widgetJsonData.src, options.baseUrl);
            this.renderImage(content, { ...widgetJsonData, src: resolvedSrc });
        } else {
            this.removeImage(content);
            this.renderPlaceholder(content);
        }

        return content;
    }

    private resolveUrl(src: string, baseUrl?: string): string {
        if (baseUrl && src.startsWith('/')) {
            return `${baseUrl}${src}`;
        }
        return src;
    }

    private renderPlaceholder(content: HTMLElement): void {
        let notAvailableElement = content.querySelector<HTMLDivElement>('.not-available');
        if (!notAvailableElement) {
            notAvailableElement = document.createElement('div');
            notAvailableElement.classList.add('not-available');
            content.appendChild(notAvailableElement);
        }
    }

    private removePlaceholder(content: HTMLElement): void {
        const notAvailableElement = content.querySelector<HTMLDivElement>('.not-available');
        notAvailableElement?.remove();
    }

    private renderImage(element: HTMLElement, data: ImageWidgetJsonData): void {
        let img = element.querySelector<HTMLImageElement>('img');
        if (!img) {
            img = document.createElement('img');
            img.classList.add('image');
        }

        if (img.getAttribute('src') !== data.src) {
            img.src = data.src || '';
        }
        img.alt = data.alt || '';

        this.applyImageStyles(img, element, data);

        element.appendChild(img);
    }

    private applyImageStyles(img: HTMLImageElement, container: HTMLElement, data: ImageWidgetJsonData): void {
        const style = data.style;
        const isContainMode = (style?.fitMode ?? 'cover') === 'contain';

        img.style.objectFit = isContainMode ? 'contain' : 'cover';
        img.style.objectPosition = '';

        this.applyImageSize(img, container, isContainMode);
        img.onload = () => this.applyImageSize(img, container, isContainMode);

        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = this.toJustifyContent(style?.imageAlignment);

        const r = style?.borderRadius;
        img.style.borderRadius = r
            ? `${r.topLeftPx ?? 0}px ${r.topRightPx ?? 0}px ${r.bottomRightPx ?? 0}px ${r.bottomLeftPx ?? 0}px`
            : '';

        const thickness = style?.borderThickness ?? 0;
        if (thickness > 0) {
            img.style.borderWidth = `${thickness}px`;
            img.style.borderStyle = 'solid';
            img.style.borderColor = style?.borderColor ?? '';
        } else {
            img.style.borderWidth = '';
            img.style.borderStyle = '';
            img.style.borderColor = '';
        }
    }

    private applyImageSize(img: HTMLImageElement, container: HTMLElement, isContainMode: boolean): void {
        if (!isContainMode) {
            img.style.width = '100%';
            img.style.height = '100%';
            return;
        }

        const mainAspect = this.calculateMainAspect(container, img);
        switch (mainAspect) {
            case ImageMainAspect.Width:
                img.style.width = 'auto';
                img.style.height = '100%';
                break;
            case ImageMainAspect.Height:
                img.style.width = '100%';
                img.style.height = 'auto';
                break;
            default:
                img.style.width = '100%';
                img.style.height = '100%';
        }
    }

    private toJustifyContent(alignment: IImageWidgetStyle['imageAlignment']): string {
        switch (alignment) {
            case 'start': {
                return 'flex-start';
            }
            case 'end': {
                return 'flex-end';
            }
            default: {
                return 'center';
            }
        }
    }

    private calculateMainAspect(container: HTMLElement, img: HTMLImageElement): ImageMainAspect {
        if (
            container.offsetWidth === 0 || container.offsetHeight === 0 ||
            img.naturalWidth === 0 || img.naturalHeight === 0
        ) {
            return ImageMainAspect.None;
        }

        const containerRatio = container.offsetWidth / container.offsetHeight;
        const imageRatio = img.naturalWidth / img.naturalHeight;

        return containerRatio >= imageRatio ? ImageMainAspect.Width : ImageMainAspect.Height;
    }

    private removeImage(element: HTMLElement): void {
        const img = element.querySelector<HTMLImageElement>('img');
        img?.remove();
    }

    protected override applyAdditionalAttributes(element: HTMLElement, widgetJsonData: ImageWidgetJsonData): void {
        this.renderHeightStyles(element, widgetJsonData.style);
    }

    private renderHeightStyles(element: HTMLElement, style: IImageWidgetStyle | undefined): void {
        const content = element.querySelector<HTMLElement>('.element-content');

        if (style?.heightFixed !== false) {
            element.style.height = style?.height ?? '';
            element.style.aspectRatio = '';
            element.style.position = '';
            element.classList.remove('aspect-ratio-resizable');
            if (content) {
                content.style.position = '';
                content.style.top = '';
                content.style.left = '';
                content.style.bottom = '';
                content.style.right = '';
            }
        } else {
            const aspectRatio = style.aspectRatio ?? 16 / 9;
            element.style.aspectRatio = String(aspectRatio);
            element.style.height = '';
            element.style.position = 'relative';
            element.classList.add('aspect-ratio-resizable');
            if (content) {
                content.style.position = 'absolute';
                content.style.top = '0';
                content.style.left = '0';
                content.style.bottom = '0';
                content.style.right = '0';
            }
        }
    }

    protected override applyVisualProperties(element: HTMLElement, data: ImageWidgetJsonData): void {
        this.rerender(element, data);
    }
}

enum ImageMainAspect {
    Width,
    Height,
    None,
}
